import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import {
    ControlContainer,
    FormArray,
    FormControl,
    FormGroup,
    FormGroupDirective,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatToolbarModule } from '@angular/material/toolbar';
import { PhoneInputComponent } from '@cargos/sprintpay-ui';
import { Subject, takeUntil } from 'rxjs';
import { InvalidFieldComponent } from '../../../../shared/components/invalid-field/invalid-field.component';
import { SectionHeaderComponent } from '../../../../shared/components/section-header/section-header.component';
import { WorkingHoursForm } from '../../../../shared/models/organization-data.model';
import { blackListedCountryCodes } from '../../../../shared/utils/constants';
import { FORM_SECTION_DESCRIPTIONS } from '../../const/form-help-text.const';
import { AppContactPhoneForm, AppForm } from '../../models/app-form';
import { timeRangeValidator } from '../../validators/validators';
import { DayRange, WorkingRangeControls, WorkingRangeFormErrorJSON } from './models/working-schedule';
import { formWorkingRangeErrors } from './utils/form-errors';

@Component({
    selector: 'app-organization-details-section',
    imports: [
        InvalidFieldComponent,
        SectionHeaderComponent,
        PhoneInputComponent,
        MatToolbarModule,
        MatSidenavModule,
        MatIconModule,
        MatButtonModule,
        MatInputModule,
        MatDividerModule,
        MatSlideToggleModule,
        MatExpansionModule,
        MatProgressSpinnerModule,
        MatCardModule,
        MatSelectModule,
        MatCardModule,
        MatRadioModule,
        ReactiveFormsModule,
    ],
    templateUrl: './organization-details-section.component.html',
    styleUrls: ['../../form-app.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class OrganizationDetailsSectionComponent implements AfterViewInit, OnDestroy {
    private unsubscribe$: Subject<void> = new Subject<void>();
    public workingRangeErrors: WorkingRangeFormErrorJSON;
    public blackListedCountryCodes: string[] = blackListedCountryCodes;
    public readonly sectionDescription = FORM_SECTION_DESCRIPTIONS.orgSettings;
    public initialCountryCode: string = 'US';
    public initialPhone: string = '';
    public weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    @ViewChild(PhoneInputComponent) phoneInputComponent?: PhoneInputComponent;

    constructor(private parent: FormGroupDirective) {
        this.workingRangeErrors = formWorkingRangeErrors;
    }

    get parentForm(): FormGroup<AppForm> {
        return this.parent?.form;
    }

    // Helper to access contactPhone group safely
    private get contactPhoneGroup(): FormGroup<AppContactPhoneForm> {
        return this.parentForm.controls.contactPhone;
    }

    private get contactPhoneCodeControl(): FormControl<string | null> {
        return this.contactPhoneGroup.controls.code;
    }

    private get contactPhonePhoneControl(): FormControl<string | null> {
        return this.contactPhoneGroup.controls.phone;
    }

    get organizationNameControl(): FormControl<string | null> {
        return this.parentForm.controls.organizationName;
    }

    get emailControl(): FormControl<string | null> {
        return this.parentForm.controls.email;
    }

    get ranges(): FormArray<FormGroup<WorkingRangeControls>> {
        return this.parentForm.controls.workingScheduleForm.controls.ranges;
    }

    get timeZone(): FormControl<string | null> {
        return this.parentForm.controls.workingScheduleForm.controls.timeZone;
    }

    ngAfterViewInit(): void {
        this.subscribeContactPhone();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    removeRange(index: number): void {
        this.ranges.removeAt(index);
    }

    addRange(): void {
        this.ranges.updateValueAndValidity();

        if (this.ranges.invalid) {
            this.ranges.markAllAsTouched();
            return;
        }

        this.ranges.push(this.createWorkingRange());
    }

    createWorkingRange(): FormGroup<WorkingRangeControls> {
        return new FormGroup(
            {
                daysRange: this.createDayRange(),
                start: new FormControl('', Validators.required),
                end: new FormControl('', Validators.required),
            },
            { validators: timeRangeValidator }
        );
    }

    createDayRange(): FormGroup<DayRange> {
        return new FormGroup({
            from: new FormControl('', Validators.required),
            to: new FormControl('', Validators.required),
        });
    }

    public updateWorkingScheduleFormAndPhone(
        workingScheduleForm: WorkingHoursForm | undefined,
        contactPhone: string | undefined,
        contactPhoneCode: string | undefined
    ): void {
        this.phoneInputComponent?.phoneForm.patchValue({
            phone: contactPhone || '',
            code: contactPhoneCode || '+1',
        });

        const rangesArray = this.ranges;
        const rangesData = workingScheduleForm?.ranges || [];

        while (rangesArray.length < rangesData.length) {
            rangesArray.push(this.createWorkingRange());
        }
        while (rangesArray.length > rangesData.length) {
            rangesArray.removeAt(rangesArray.length - 1);
        }

        rangesData.forEach((range, index) => {
            rangesArray.at(index).patchValue({
                daysRange: {
                    from: range.daysRange?.from || '',
                    to: range.daysRange?.to || '',
                },
                start: range.start || '',
                end: range.end || '',
            });
        });

        rangesArray.updateValueAndValidity();

        const timeZoneControl = this.timeZone;
        if (timeZoneControl) {
            timeZoneControl.patchValue(workingScheduleForm?.timeZone || '');
        }
        this.parentForm.patchValue({
            workingScheduleForm: {
                timeZone: workingScheduleForm?.timeZone || '',
            },
            contactPhone: {
                phone: contactPhone || '',
                code: contactPhoneCode || '+1',
            },
        });
    }

    subscribeContactPhone(): void {
        const codeControl = this.contactPhoneCodeControl;
        const phoneControl = this.contactPhonePhoneControl;

        if (codeControl) {
            codeControl.setValue(this.phoneInputComponent?.phoneForm.controls['code'].value ?? '+1');
        }

        this.phoneInputComponent?.phoneForm
            .get('code')
            ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
            .subscribe((code) => {
                if (codeControl) codeControl.setValue(code);
            });

        this.phoneInputComponent?.phoneForm
            .get('phone')
            ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
            .subscribe((phone) => {
                if (phoneControl) phoneControl.setValue(phone);
            });
    }
}
